import { useState, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  FormLabel,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'notistack';
import {
  fetchPartnershipById,
  createPartnership,
  updatePartnership,
  clearCurrent,
} from '../../store/slices/partnershipSlice';
import * as partnershipApi from '../../api/partnershipApi';
import { getPartnershipTypeLabel } from '../../utils/partnershipUtils';

const PARTNERSHIP_TYPES = [
  { value: 'academic', label: 'Académique' },
  { value: 'research', label: 'Recherche' },
  { value: 'industry', label: 'Industrie' },
  { value: 'other', label: 'Autre' },
];

export default function PartnershipForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((s) => s.auth);
  const { current: item, loading } = useSelector((s) => s.partnership);

  const [form, setForm] = useState({
    name: '',
    description: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    website: '',
    partnershipType: 'other',
    startDate: '',
    endDate: '',
    contract: null,
    notes: '',
  });
  const [noEndDate, setNoEndDate] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [contractUploading, setContractUploading] = useState(false);

  useEffect(() => {
    if (isEdit) dispatch(fetchPartnershipById(id));
    return () => dispatch(clearCurrent());
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (item && isEdit) {
      setForm({
        name: item.name || '',
        description: item.description || '',
        contactName: item.contactName || '',
        contactEmail: item.contactEmail || '',
        contactPhone: item.contactPhone || '',
        address: item.address || '',
        website: item.website || '',
        partnershipType: item.partnershipType || 'other',
        startDate: item.startDate ? new Date(item.startDate).toISOString().slice(0, 10) : '',
        endDate: item.endDate ? new Date(item.endDate).toISOString().slice(0, 10) : '',
        contract: item.contract || null,
        notes: item.notes || '',
      });
      setNoEndDate(!item.endDate);
    }
  }, [item, isEdit]);

  const allowed = ['admin', 'moderator'].includes(user?.role);
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleContractChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Format non autorisé. Utilisez PDF, Word ou une image.', { variant: 'error' });
      return;
    }
    setContractUploading(true);
    try {
      const result = await partnershipApi.uploadPartnershipContract(file);
      updateField('contract', {
        url: result.url,
        publicId: result.publicId,
        filename: result.filename || file.name,
        mimetype: result.mimetype,
        resourceType: result.resourceType || (file.type.startsWith('image/') ? 'image' : 'raw'),
      });
      setContractFile(file.name);
      enqueueSnackbar('Contrat uploadé.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Erreur lors de l\'upload.', { variant: 'error' });
    } finally {
      setContractUploading(false);
    }
    e.target.value = '';
  };

  const handleRemoveContract = () => {
    updateField('contract', null);
    setContractFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      enqueueSnackbar('Le nom du partenaire est requis.', { variant: 'error' });
      return;
    }
    if (!form.startDate) {
      enqueueSnackbar('La date de début est requise.', { variant: 'error' });
      return;
    }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      contactName: form.contactName.trim() || undefined,
      contactEmail: form.contactEmail.trim() || undefined,
      contactPhone: form.contactPhone.trim() || undefined,
      address: form.address.trim() || undefined,
      website: form.website.trim() || undefined,
      partnershipType: form.partnershipType,
      startDate: form.startDate,
      endDate: noEndDate ? undefined : form.endDate || undefined,
      contract: form.contract || undefined,
      notes: form.notes.trim() || undefined,
    };
    if (isEdit) {
      const result = await dispatch(updatePartnership({ id, ...payload }));
      if (updatePartnership.fulfilled.match(result)) {
        enqueueSnackbar('Partenariat mis à jour.', { variant: 'success' });
        navigate('/dashboard/partnerships');
      } else {
        enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
      }
    } else {
      const result = await dispatch(createPartnership(payload));
      if (createPartnership.fulfilled.match(result)) {
        enqueueSnackbar('Partenariat créé.', { variant: 'success' });
        navigate('/dashboard/partnerships');
      } else {
        enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/partnerships')}>
          Retour
        </Button>
      </Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        {isEdit ? 'Modifier le partenariat' : 'Nouveau partenariat'}
      </Typography>
      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nom du partenaire *"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                label="Contact"
                value={form.contactName}
                onChange={(e) => updateField('contactName', e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                label="E-mail"
                type="email"
                value={form.contactEmail}
                onChange={(e) => updateField('contactEmail', e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                label="Téléphone"
                value={form.contactPhone}
                onChange={(e) => updateField('contactPhone', e.target.value)}
                sx={{ flex: 1, minWidth: 160 }}
              />
            </Box>
            <TextField
              label="Adresse"
              value={form.address}
              onChange={(e) => updateField('address', e.target.value)}
              fullWidth
            />
            <TextField
              label="Site web"
              value={form.website}
              onChange={(e) => updateField('website', e.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Type de partenariat"
              value={form.partnershipType}
              onChange={(e) => updateField('partnershipType', e.target.value)}
              SelectProps={{ native: true }}
              sx={{ maxWidth: 280 }}
            >
              {PARTNERSHIP_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Date de début *"
                type="date"
                value={form.startDate}
                onChange={(e) => updateField('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                sx={{ width: 180 }}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={noEndDate} onChange={(e) => setNoEndDate(e.target.checked)} />
                }
                label="Pas de date de fin"
              />
              {!noEndDate && (
                <TextField
                  label="Date de fin"
                  type="date"
                  value={form.endDate}
                  onChange={(e) => updateField('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 180 }}
                />
              )}
            </Box>
            <FormControl>
              <FormLabel sx={{ mb: 1 }}>Contrat (PDF ou image)</FormLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={contractUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  disabled={contractUploading}
                >
                  Choisir un fichier
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleContractChange}
                  />
                </Button>
                {(form.contract?.filename || contractFile) && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {form.contract?.filename || contractFile}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleRemoveContract}
                    >
                      Supprimer
                    </Button>
                  </>
                )}
              </Box>
            </FormControl>
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/dashboard/partnerships')}>
                Annuler
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
